// The respondent data view's state and networking (#1887). The page holds
// the vartree; this hook holds the view: the pulled-in variables, the quick
// filter's expression, the page and the labels/codes switch. The view lives
// in the route's query params (parseViewParams), so a reload or a shared link
// reopens it; nothing here mirrors it into state. Rows come from
// GET /job/:customer/:job/casedata/view (open to whoever may use the agent);
// the quick filter dialog checks its expression with POST .../view/validate.
import { useState } from 'react'
import axios from 'axios'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'
import { blobErrorMessage } from '../utils/blobErrorMessage'
import { composerDraftStorageKey } from '../agent/hooks/useComposerDraft'
import useRouter from './useRouter'
import {
  buildAgentDraft,
  canonicalFilter,
  parseViewParams,
  viewParams,
  type CaseDataViewAccess,
  type CaseDataViewPage,
  type CaseDataViewState,
} from '../utils/caseDataView'

/** A request the browser gave up on (a newer page superseded it) is not an error to show. */
const rethrow = (err: unknown): never => {
  if (axios.isCancel(err)) throw err
  throw new Error(extractErrorMessage(err))
}

function saveBlob(data: Blob, fileName: string) {
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function useCaseDataView({ customer, job }: { customer: string; job: string }) {
  const router = useRouter()
  const [accessOpen, setAccessOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const parsed = parseViewParams(router.params)
  // A link to a variable (a search result, an import's "view") opens the
  // view with that variable pulled in; the first change writes it into cols.
  const highlight = (router.params.highlightVariable as string | undefined)?.trim()
  const view: CaseDataViewState = parsed.columns.length === 0 && highlight ? { ...parsed, columns: [highlight] } : parsed
  const { columns, filter, offset, limit, showCodes } = view

  // Every change replaces the history entry: Back leaves the data view
  // rather than stepping back through each change.
  const setView = (patch: Partial<CaseDataViewState>) => {
    router.setParams({ customer, job, ...viewParams({ ...view, ...patch }) }, true, { history: 'replace' })
  }

  const viewUrl = `${API_BASE_URL}/job/${customer}/${job}/casedata/view`

  const pageQuery = useQuery({
    queryKey: ['casedata-view', customer, job, columns, filter, offset, limit],
    // The signal aborts a request a newer page supersedes; the server stops
    // reading when its client goes.
    queryFn: async ({ signal }) => {
      try {
        const res = await axios.get<CaseDataViewPage>(viewUrl, {
          params: { varnames: columns.join(','), filter: filter || undefined, offset, limit },
          signal,
        })
        return res.data
      } catch (err) {
        return rethrow(err)
      }
    },
    enabled: !!customer && !!job && columns.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  })

  const accessQuery = useQuery({
    queryKey: ['casedata-view-access', customer, job],
    queryFn: async () => {
      try {
        const res = await axios.get<{ accesses: CaseDataViewAccess[]; total: number }>(`${viewUrl}/access`)
        return res.data.accesses
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
    enabled: accessOpen && !!customer && !!job,
    staleTime: 0,
    retry: false,
  })

  // The server answers with each variable's file name, which can differ in
  // case from the tree's, so names compare case-insensitively.
  const sameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

  const handleAddColumn = (variable: string) => {
    if (columns.some((v) => sameName(v, variable))) return
    setView({ columns: [...columns, variable] })
  }

  const handleRemoveColumn = (variable: string) => {
    setView({ columns: columns.filter((v) => !sameName(v, variable)) })
  }

  // A page of a different filter is a different list: start it at its top.
  const handleSaveFilter = (expr: string) => setView({ filter: canonicalFilter(expr), offset: 0 })

  const handleClearFilter = () => setView({ filter: '', offset: 0 })

  /**
   * The quick filter dialog's check (FilterEditorModal `validate`): what
   * goapi can evaluate against the case data, with its refusal naming the
   * part it cannot use. Null when the expression is fine.
   */
  const validateFilter = async (forJob: string, expr: string): Promise<string | null> => {
    try {
      await axios.post(`${API_BASE_URL}/job/${customer}/${forJob}/casedata/view/validate`, { filter: expr })
      return null
    } catch (err) {
      return extractErrorMessage(err)
    }
  }

  const handlePage = (next: number) => setView({ offset: next })

  const handleLimit = (next: number) => setView({ limit: next, offset: 0 })

  const handleShowCodes = (next: boolean) => setView({ showCodes: next })

  const handleExportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const res = await axios.get(viewUrl, {
        params: { varnames: columns.join(','), filter: filter || undefined, format: 'csv', codes: showCodes ? 'true' : undefined },
        responseType: 'blob',
      })
      saveBlob(new Blob([res.data], { type: 'text/csv' }), `${job}_data_view.csv`)
    } catch (err) {
      setExportError(await blobErrorMessage(err))
    } finally {
      setExporting(false)
    }
  }

  // The start screen reads its unsent text from sessionStorage (#1836), so
  // the draft is written there and the screen opens with it in the composer.
  const handleSendToAgent = () => {
    const page = pageQuery.data
    const draft = buildAgentDraft({
      job,
      columns: columns.map((name) => page?.columns.find((c) => sameName(c.name, name)) ?? { name, description: '', data_type: 'single' }),
      filter,
      caseVariable: page?.case_variable ?? '',
      filteredCases: page?.filtered_cases,
      totalCases: page?.total_cases,
    })
    try {
      sessionStorage.setItem(composerDraftStorageKey(`start:${customer}`), draft)
    } catch {
      // Storage unavailable: the start screen opens empty.
    }
    router.navigate('agent-new', { customer })
  }

  return {
    columns,
    filter,
    offset,
    limit,
    showCodes,
    // A disabled query keeps showing its last page as a placeholder; with no
    // variables pulled in there is no page.
    page: columns.length > 0 ? pageQuery.data : undefined,
    isLoading: pageQuery.isFetching,
    error: pageQuery.error ? (pageQuery.error as Error).message : null,
    exporting,
    exportError,
    filterOpen,
    accessOpen,
    accesses: accessQuery.data ?? [],
    accessLoading: accessQuery.isFetching,
    accessError: accessQuery.error ? (accessQuery.error as Error).message : null,
    handleAddColumn,
    handleRemoveColumn,
    handleOpenFilter: () => setFilterOpen(true),
    handleCloseFilter: () => setFilterOpen(false),
    handleSaveFilter,
    handleClearFilter,
    validateFilter,
    handlePage,
    handleLimit,
    handleShowCodes,
    handleExportCsv,
    handleSendToAgent,
    handleOpenAccess: () => setAccessOpen(true),
    handleCloseAccess: () => setAccessOpen(false),
    handleDismissExportError: () => setExportError(null),
  }
}

export default useCaseDataView
